import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart3, Loader2, LogOut, Menu, Package, Plug, Receipt, Settings, Sparkles, Store, Tag, Users, X,
} from 'lucide-react';
import ThemeToggle from '../shared/ThemeToggle';
import logo from '../../assets/logos/logo.png';
import './org.css';

/**
 * Το πλαίσιο των κεντρικών (νέα εμφάνιση, 14/10/2026): κεφαλίδα + πλαϊνό μενού σε ομάδες, ΙΔΙΟ ύφος και
 * ίδιες κλάσεις με τη διαχείριση καταστήματος (shared/StoreAdminLayout + StoreNavigation) — αντί για τη
 * σειρά με τις 11 καρτέλες που δεν χωρούσαν. Ίδια στοιχεία και ίδιοι κανόνες ορατότητας ανά ρόλο με τις
 * παλιές καρτέλες (visibleOrgTabs)· οι ομάδες απλώς τα ταξινομούν.
 *
 * Η κλάση xv2-store-nav μπαίνει και εδώ: με αυτήν το RoleLight.css βάζει κεφαλίδα, πεδία και μπάρες
 * κύλισης στα tokens του θέματος (όπως στη διαχείριση καταστήματος) αντί για το λευκό του κλασικού.
 */

// «Κατάλογος πλατφορμών» και «Εκδόσεις agent» ΔΕΝ είναι πια καρτέλες των κεντρικών (απόφαση ιδιοκτήτη 15/10/2026):
// τις βλέπει μόνο ο super admin, στον δικό του πίνακα (superadmin/SuperAdminOrgTools).
export type OrgTabId =
  | 'overview' | 'orders'
  | 'catalog' | 'promotions' | 'upsell'
  | 'platforms'
  | 'stores' | 'members' | 'settings';

export type OrgNavGroupId = 'overview' | 'catalog' | 'platforms' | 'org';

export const ORG_NAV_GROUPS: ReadonlyArray<{ id: OrgNavGroupId; items: readonly OrgTabId[] }> = [
  { id: 'overview', items: ['overview', 'orders'] },
  { id: 'catalog', items: ['catalog', 'promotions', 'upsell'] },
  { id: 'platforms', items: ['platforms'] },
  { id: 'org', items: ['stores', 'members', 'settings'] },
];

const ICONS: Record<OrgTabId, React.ElementType> = {
  overview: BarChart3,
  orders: Receipt,
  catalog: Package,
  promotions: Tag,
  upsell: Sparkles,
  platforms: Plug,
  stores: Store,
  members: Users,
  settings: Settings,
};

/**
 * Οι καρτέλες που βλέπει ένας ρόλος — ΙΔΙΟΙ κανόνες με πριν: ο παρατηρητής (π.χ. franchise με δικά του
 * καταστήματα) βλέπει μόνο νούμερα· «Ρυθμίσεις» μόνο ιδιοκτήτης/διαχειριστής. «Κατάλογος πλατφορμών» και
 * «Εκδόσεις agent»: μόνο super admin (15/10/2026), όχι εδώ.
 */
export function visibleOrgTabs(myRole: string): OrgTabId[] {
  const canManage = myRole === 'org_owner' || myRole === 'org_admin';
  const tabs: OrgTabId[] = ['overview', 'stores', 'orders'];
  if (myRole !== 'org_viewer') tabs.push('members', 'catalog', 'promotions', 'upsell', 'platforms');
  if (canManage) tabs.push('settings');
  return tabs;
}

/** Οι ομάδες με τα ορατά τους στοιχεία, στη σειρά του μενού· ομάδα χωρίς στοιχείο δεν εμφανίζεται. */
export function groupOrgTabs(tabs: OrgTabId[]): Array<{ id: OrgNavGroupId; items: OrgTabId[] }> {
  return ORG_NAV_GROUPS
    .map(group => ({ id: group.id, items: group.items.filter(id => tabs.includes(id)) }))
    .filter(group => group.items.length > 0);
}

export interface OrgShellProps {
  /** Όνομα οργανισμού (ή «Οργανισμοί» όταν δεν υπάρχει κανένας). */
  title: string;
  /** «27 καταστήματα · 4 μέλη». */
  meta?: string | null;
  orgs: Array<{ id: number; name: string }>;
  selectedOrgId: number | null;
  onSelectOrg: (orgId: number) => void;
  userName?: string | null;
  /** Ο ρόλος του συνδεδεμένου στον οργανισμό. */
  userRole?: string | null;
  onLogout: () => void;
  /** Ορατές καρτέλες· κενό = χωρίς μενού (κανένας οργανισμός, ή φορτώνει). */
  tabs: OrgTabId[];
  activeTab: OrgTabId;
  onTabClick: (tab: OrgTabId) => void;
  /** Όσο φορτώνει ο οργανισμός που επιλέχθηκε. */
  busy?: boolean;
  children: React.ReactNode;
}

export const OrgShell: React.FC<OrgShellProps> = ({
  title, meta, orgs, selectedOrgId, onSelectOrg, userName, userRole, onLogout, tabs, activeTab, onTabClick, busy = false,
  children,
}) => {
  const { t } = useTranslation(['orgDashboard', 'admin', 'common']);
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const sideNavRef = useRef<HTMLElement>(null);

  // Συρτάρι κινητού: κλείνει με κλικ έξω και Esc, και η σελίδα από κάτω δεν κυλά (όπως το StoreNavigation).
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) setMobileOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  // Το πλαϊνό μενού έχει δική του κύλιση: η ενεργή επιλογή στο μέσο αν δεν φαίνεται.
  useEffect(() => {
    const nav = sideNavRef.current;
    const active = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !active || typeof active.getBoundingClientRect !== 'function') return;
    const navBox = nav.getBoundingClientRect();
    const box = active.getBoundingClientRect();
    if (box.top < navBox.top || box.bottom > navBox.bottom) {
      nav.scrollTop += box.top - navBox.top - (navBox.height - box.height) / 2;
    }
  }, [activeTab, tabs.length]);

  const groupLabels: Record<OrgNavGroupId, string> = {
    overview: t('orgDashboard:nav.groups.overview'),
    catalog: t('orgDashboard:nav.groups.catalog'),
    platforms: t('orgDashboard:nav.groups.platforms'),
    org: t('orgDashboard:nav.groups.org'),
  };
  const itemLabel = (id: OrgTabId): string => t(`orgDashboard:nav.items.${id}`);
  const groups = groupOrgTabs(tabs);
  const hasNav = groups.length > 0;
  const ActiveIcon = ICONS[activeTab] || BarChart3;

  const select = (tab: OrgTabId) => {
    setMobileOpen(false);
    onTabClick(tab);
  };

  const renderGroups = (where: 'side' | 'drawer') => groups.map(group => {
    const headingId = `org-nav-${where}-${group.id}`;
    return (
      <div key={group.id} role="group" aria-labelledby={headingId}>
        {/* role="heading" αντί για <h2>: το RoleLight.css αλλάζει το letter-spacing των h1–h3 */}
        <div id={headingId} role="heading" aria-level={2} className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-t4">
          {groupLabels[group.id]}
        </div>
        <ul className="space-y-0.5">
          {group.items.map(id => {
            const Icon = ICONS[id];
            const isActive = activeTab === id;
            return (
              <li key={id}>
                <button
                  type="button"
                  data-tab={id}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => select(id)}
                  className={`w-full flex items-center gap-2.5 px-3 rounded-lg text-left text-sm transition-colors ${
                    where === 'side' ? 'h-8' : 'h-11'
                  } ${
                    isActive ? 'bg-brand-soft text-brand font-semibold' : 'text-t2 font-medium hover:bg-surface-3 hover:text-t1'
                  }`}
                >
                  <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-brand' : 'text-t3'}`} aria-hidden="true" />
                  <span className="truncate">{itemLabel(id)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  });

  // Επιλογή οργανισμού: μόνο με >1 (όπως πριν). Στο κινητό μέσα στο συρτάρι — η κεφαλίδα δεν χωρά.
  const orgSelect = (where: 'header' | 'drawer') => (orgs.length > 1 ? (
    <select
      aria-label={t('orgDashboard:shell.selectOrg')}
      value={selectedOrgId ?? ''}
      onChange={e => {
        const id = parseInt(e.target.value, 10);
        if (Number.isFinite(id)) {
          setMobileOpen(false);
          onSelectOrg(id);
        }
      }}
      data-testid={`org-select-${where}`}
      className={where === 'header'
        ? 'hidden sm:block h-9 max-w-[220px] pl-3 pr-8 rounded-lg border border-line-strong bg-surface-2 text-sm text-t1 truncate'
        : 'w-full h-11 pl-3 pr-8 rounded-xl border border-line-strong bg-surface-2 text-sm text-t1'}
    >
      {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  ) : null);

  return (
    <div className="min-h-screen bg-surface-0" data-testid="org-shell">
      <header className="role-header bg-surface-1 border-b border-line">
        <div className="w-full px-3 sm:px-4">
          <div className="flex items-center justify-between gap-3 h-14">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img src={logo} alt="" aria-hidden="true" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg shrink-0" />
              <div className="min-w-0">
                <p className="text-base sm:text-lg font-bold text-t1 truncate flex items-center gap-2" data-testid="org-shell-title">
                  <span className="truncate">{title}</span>
                  {busy && <Loader2 className="w-4 h-4 shrink-0 animate-spin text-t3" aria-label={t('common:loading')} />}
                </p>
                {meta && <p className="text-xs text-t3 truncate">{meta}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              {orgSelect('header')}
              <ThemeToggle />
              {userName && (
                <div className="text-right hidden sm:block min-w-0 max-w-[200px]">
                  <p className="text-sm font-medium text-t1 truncate" data-testid="org-shell-user">{userName}</p>
                  {userRole && <p className="text-xs text-t3 truncate">{userRole}</p>}
                </div>
              )}
              <button
                type="button"
                onClick={onLogout}
                className="w-9 h-9 rounded-lg bg-surface-2 hover:bg-red-600/20 text-t3 hover:text-red-600 dark:hover:text-red-400 flex items-center justify-center transition-all active:scale-95"
                title={t('admin:logout2')}
                aria-label={t('admin:logout2')}
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {hasNav ? (
        <div className="md:flex">
          {/* ===== DESKTOP: πλαϊνό μενού σε ομάδες — όπως το StoreNavigation (νέα εμφάνιση) =====
              Η στήλη τεντώνεται σε όλο το ύψος της σελίδας· το εσωτερικό μένει κολλημένο πάνω, με δική
              του κύλιση, και χωρά κάτω από την κεφαλίδα (h-14 + 1px περίγραμμα). */}
          <aside className="xv2-store-nav hidden md:block w-60 shrink-0 bg-surface-2 border-r border-line" data-testid="org-nav">
            <div className="sticky top-0 flex flex-col h-[calc(100vh-3.5rem-1px)]">
              <nav ref={sideNavRef} aria-label={t('common:navigation')} className="flex-1 min-h-0 overflow-y-auto px-2 py-4 space-y-4">
                {renderGroups('side')}
              </nav>
            </div>
          </aside>

          {/* ===== MOBILE: μπάρα με την τρέχουσα σελίδα + συρτάρι ===== */}
          <div className="xv2-store-nav md:hidden bg-surface-2 border-b border-line sticky top-0 z-40">
            <div className="flex items-center justify-between px-4 h-12">
              <div className="flex items-center gap-2 min-w-0">
                <ActiveIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                <span className="text-sm font-semibold text-t1 truncate">{itemLabel(activeTab)}</span>
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                aria-label={t('common:navigation')}
                aria-expanded={mobileOpen}
                className="w-9 h-9 rounded-lg border border-line text-t2 hover:bg-surface-3 flex items-center justify-center transition-colors active:scale-95"
                data-testid="org-nav-open"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          {mobileOpen && (
            <div className="md:hidden fixed inset-0 z-50">
              <div className="absolute inset-0 bg-black/40" />
              <div
                ref={drawerRef}
                role="dialog"
                aria-modal="true"
                aria-label={t('common:navigation')}
                className="org-drawer absolute right-0 top-0 h-full w-72 max-w-[85vw] bg-surface-2 border-l border-line shadow-2xl flex flex-col"
              >
                <div className="flex items-center justify-between px-4 h-14 border-b border-line shrink-0">
                  <span className="text-sm font-semibold text-t1">{t('common:navigation')}</span>
                  <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })}
                    className="w-9 h-9 rounded-lg text-t2 hover:bg-surface-3 flex items-center justify-center transition-colors active:scale-95"
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
                {orgs.length > 1 && <div className="px-4 pt-4 shrink-0">{orgSelect('drawer')}</div>}
                <nav aria-label={t('common:navigation')} className="flex-1 overflow-y-auto px-2 py-4 space-y-4">
                  {renderGroups('drawer')}
                </nav>
              </div>
            </div>
          )}

          <main className="w-full min-w-0 flex-1 px-4 py-4 md:px-6 md:py-5" data-testid="org-main">{children}</main>
        </div>
      ) : (
        <main className="w-full min-w-0 px-4 py-4 md:px-6 md:py-5" data-testid="org-main">{children}</main>
      )}
    </div>
  );
};

export default OrgShell;
